import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Divider, Grid, Group, LoadingOverlay, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { useAppDispatch, useAppSelector } from '../../../store'
import { PackageType } from '../../../store/types'
import { BackListButton, ContentBox, ItemActionsMenu } from '../../partials'
import { fetchShowPackage, resetShowPackage } from '../../../store/features/packages/showPackageSlice'

const Show = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [data, setData] = useState<PackageType | null>(null)
  const { isLoading, response, error } = useAppSelector(state => state.showPackage)
  const {
    isLoading: deleteIsLoading,
    response: deleteResponse,
    error: deleteError
  } = useAppSelector(state => state.deletePackage)

  useEffect(() => {
    if (response === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowPackage(id as string))
      })()
    }
  }, [response, data, id, dispatch])

  useEffect(() => {
    if (response !== null) {
      setData(response.data)

      if (data !== null) {
        dispatch(resetShowPackage())
      }
    }
  }, [response, dispatch, data])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowPackage())
          await dispatch(fetchShowPackage(id))
        })()
      } else if (error.response.status === 404) {
        dispatch(resetShowPackage())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(error.data.message)
        })

        navigate('/users')
      } else {
        dispatch(resetShowPackage())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/packages')
      }
    }
  }, [error, id, dispatch, t, navigate])

  useEffect(() => {
    if (deleteResponse !== null) {
      dispatch(resetShowPackage())
      // dispatch(resetDeletePackage())

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('deleted')
      })

      navigate('/packages')
    }
  }, [deleteResponse, t, dispatch, navigate])

  useEffect(() => {
    if (deleteError !== null) {
      dispatch(resetShowPackage())

      showNotification({
        color: 'red',
        title: t('unsuccessful'),
        message: deleteError.response.data ? deleteError.response.data.message : t('errors.unknown')
      })
    }
  }, [deleteError, t, dispatch])

  const handleDelete = async (id: string) => {
    //await dispatch(fetchDeletePackage(id))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={isLoading || deleteIsLoading} />
      {data && (
        <>
          <ContentBox title={t('content_actions.show')}>
            <h4>{t('id')}:</h4>
            <Text>{data.id}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('name')}:</h4>
            <Text>{data.name}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('description')}:</h4>
            <Text>{data.description}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('pages.package.fleet_size')}:</h4>
            <Text>{data.fleet_size ?? '-'}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('pages.package.annual_fee')}:</h4>
            <Text>${data.annual_fee}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('pages.package.monthly_fee')}:</h4>
            <Text>${data.monthly_fee}</Text>
          </ContentBox>
          <ContentBox>
            <Grid>
              <Grid.Col span={6}>
                <BackListButton to="/packages" />
              </Grid.Col>
              <Grid.Col span={6}>
                <Group
                  position="right"
                  pr="md"
                >
                  <ItemActionsMenu
                    edit={`/packages/${data.id}/edit`}
                    onDelete={() => handleDelete(data.id)}
                  />
                </Group>
              </Grid.Col>
            </Grid>
          </ContentBox>
        </>
      )}
    </MainLayout>
  )
}

export default Show
